/**
 * Standard Page Layout Component
 * 
 * Reusable template for management pages with:
 * - Header with title, subtitle, and action buttons
 * - Toolbar with refresh/action buttons
 * - Content area for tables/lists
 * - Pagination controls
 * - Loading and error states
 * 
 * @component
 * @example
 * <StandardPage
 *   header={{
 *     title: 'Users',
 *     subtitle: 'Manage system users',
 *     actions: [{ label: 'New User', onClick: handleCreate }]
 *   }}
 *   pagination={{
 *     state: { pageIndex: 0, pageSize: 10 },
 *     data: { total: 50, totalPages: 5, page: 1, pageSize: 10 },
 *     onChange: setPagination
 *   }}
 *   isLoading={isLoading}
 *   onRefetch={refetch}
 * >
 *   <UserTable users={users} />
 * </StandardPage>
 */

import { FC } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import type { StandardPageProps } from './StandardPage.types';
import styles from './StandardPage.module.css';

const StandardPage: FC<StandardPageProps> = ({
  header,
  toolbarButtons,
  children,
  isLoading = false,
  isError = false,
  error,
  onRefetch,
  pagination,
  loadingMessage = 'Carregando dados...',
  errorMessage = 'Erro ao carregar os dados',
}) => {
  return (
    <div className={styles.container}>
      {/* ===== HEADER ===== */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>{header.title}</h1>
          {header.subtitle && (
            <p className={styles.subtitle}>{header.subtitle}</p>
          )}
        </div>

        {/* Header Actions */}
        {header.actions && header.actions.length > 0 && (
          <div className={styles.headerRight}>
            {header.actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'primary'}
                iconLeft={action.icon}
                onClick={action.onClick}
                disabled={action.disabled || action.loading}
                className={styles.button}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* ===== TOOLBAR ===== */}
      {(toolbarButtons || onRefetch) && (
        <div className={styles.toolbar}>
          <div className={styles.toolbarActions}>
            {/* Refresh Button (always included if onRefetch provided) */}
            {onRefetch && (
              <button
                className={styles.toolbarButton}
                onClick={onRefetch}
                disabled={isLoading}
                aria-label="Atualizar"
                title="Atualizar dados"
              >
                <FiRefreshCw size={18} />
                Atualizar
              </button>
            )}

            {/* Additional Toolbar Buttons */}
            {toolbarButtons &&
              toolbarButtons.map((button, index) => (
                <button
                  key={index}
                  className={styles.toolbarButton}
                  onClick={button.onClick}
                  disabled={button.disabled || button.loading}
                  aria-label={button.label}
                >
                  {button.icon && <span>{button.icon}</span>}
                  {button.label}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* ===== CONTENT ===== */}
      <div className={styles.content}>
        {/* Loading State */}
        {isLoading && (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner} />
            <p className={styles.loadingMessage}>{loadingMessage}</p>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>
              ❌ {error?.message || errorMessage}
            </p>
            {onRefetch && (
              <button
                className={styles.errorButton}
                onClick={onRefetch}
              >
                🔄 Tentar novamente
              </button>
            )}
          </div>
        )}

        {/* Main Content */}
        {!isLoading && !isError && (
          <>
            {children}

            {/* ===== PAGINATION ===== */}
            {pagination && (
              <div className={styles.tableFooter}>
                <div className={styles.pagination}>
                  <div className={styles.paginationInfo}>
                    Mostrando{' '}
                    {pagination.state.pageIndex *
                      pagination.state.pageSize +
                      1}{' '}
                    a{' '}
                    {Math.min(
                      (pagination.state.pageIndex + 1) *
                        pagination.state.pageSize,
                      pagination.data.total
                    )}{' '}
                    de {pagination.data.total}
                  </div>
                  <div className={styles.paginationControls}>
                    {/* First Page */}
                    <button
                      className={styles.paginationButton}
                      onClick={() =>
                        pagination.onChange({
                          ...pagination.state,
                          pageIndex: 0,
                        })
                      }
                      disabled={pagination.state.pageIndex === 0}
                      aria-label="Primeira página"
                    >
                      ««
                    </button>

                    {/* Previous Page */}
                    <button
                      className={styles.paginationButton}
                      onClick={() =>
                        pagination.onChange({
                          ...pagination.state,
                          pageIndex:
                            pagination.state.pageIndex - 1,
                        })
                      }
                      disabled={pagination.state.pageIndex === 0}
                      aria-label="Página anterior"
                    >
                      «
                    </button>

                    {/* Page Info */}
                    <span className={styles.pageInfo}>
                      Página {pagination.state.pageIndex + 1}{' '}
                      de {pagination.data.totalPages}
                    </span>

                    {/* Next Page */}
                    <button
                      className={styles.paginationButton}
                      onClick={() =>
                        pagination.onChange({
                          ...pagination.state,
                          pageIndex:
                            pagination.state.pageIndex + 1,
                        })
                      }
                      disabled={
                        pagination.state.pageIndex >=
                        pagination.data.totalPages - 1
                      }
                      aria-label="Próxima página"
                    >
                      »
                    </button>

                    {/* Last Page */}
                    <button
                      className={styles.paginationButton}
                      onClick={() =>
                        pagination.onChange({
                          ...pagination.state,
                          pageIndex:
                            pagination.data.totalPages - 1,
                        })
                      }
                      disabled={
                        pagination.state.pageIndex >=
                        pagination.data.totalPages - 1
                      }
                      aria-label="Última página"
                    >
                      »»
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StandardPage;
